
import React, { useState } from 'react';
import { Staff, Location, OpeningHours } from '../types';
import { LOCATIONS } from '../constants';

interface LocationSettingsViewProps {
  staff: Staff[];
  setStaff: React.Dispatch<React.SetStateAction<Staff[]>>;
}

const LocationSettingsView: React.FC<LocationSettingsViewProps> = ({ staff, setStaff }) => {
  const [selectedLocationId, setSelectedLocationId] = useState<string>(LOCATIONS[0].id);
  const [activeTab, setActiveTab] = useState<'hours' | 'staff'>('hours');

  const selectedLocation = LOCATIONS.find(l => l.id === selectedLocationId) || LOCATIONS[0];
  const assignedStaff = staff.filter(s => s.assignedLocationIds.includes(selectedLocationId));
  const availableStaff = staff.filter(s => !s.assignedLocationIds.includes(selectedLocationId));

  const toggleStaffAssignment = (staffId: string) => {
    setStaff(prev => prev.map(s => {
      if (s.id === staffId) {
        const isAssigned = s.assignedLocationIds.includes(selectedLocationId);
        return {
          ...s,
          assignedLocationIds: isAssigned 
            ? s.assignedLocationIds.filter(id => id !== selectedLocationId)
            : [...s.assignedLocationIds, selectedLocationId]
        };
      }
      return s;
    }));
  };

  return (
    <div className="space-y-8 md:space-y-12 pb-16">
      <div className="section-header flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="space-y-1">
          <h2 className="ymca-brand-font text-5xl md:text-6xl uppercase leading-none">Site Management</h2>
          <p className="text-gray-500 font-medium uppercase text-[10px] md:text-xs tracking-[0.2em] mt-2">
            Configure Divisional Sites, Opening Hours, and Authorised Staff
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 md:gap-12">
        {/* Left: Site List */}
        <div className="lg:col-span-1 border-2 border-black bg-white overflow-hidden">
          <div className="p-4 bg-black text-white text-[10px] font-black uppercase tracking-widest">Select Site</div>
          <div className="max-h-[600px] overflow-y-auto divide-y divide-black/5 scrollbar-hide">
            {LOCATIONS.map(loc => (
              <button
                key={loc.id}
                onClick={() => setSelectedLocationId(loc.id)}
                className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${selectedLocationId === loc.id ? 'bg-ymca-neutral border-l-4 border-ymca-orange' : 'border-l-4 border-transparent'}`}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className="ymca-brand-font text-xl uppercase leading-none">{loc.name}</span>
                  {loc.status === 'Not In Service' && (
                    <span className="text-[6px] font-black bg-red-100 text-red-600 px-1 py-0.5 uppercase tracking-tighter">Inactive</span>
                  )}
                </div>
                <div className="flex justify-between text-[8px] font-bold text-gray-400 uppercase tracking-widest">
                  <span>{loc.type}</span>
                  <span>{loc.address}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right: Site Details & Config */}
        <div className="lg:col-span-3 space-y-8">
          <div className="bg-white border-2 border-black p-6 md:p-10 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
            <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-10 border-b-2 border-black pb-6">
              <div>
                <h3 className="ymca-brand-font text-4xl md:text-5xl uppercase leading-none">{selectedLocation.name}</h3>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-2">ID: {selectedLocation.id} • {selectedLocation.type}</p>
              </div>
              <div className="flex bg-ymca-neutral p-1 border border-black/10">
                <button 
                  onClick={() => setActiveTab('hours')}
                  className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'hours' ? 'bg-black text-white' : 'text-gray-400 hover:text-black'}`}
                >
                  Operating Hours
                </button>
                <button 
                  onClick={() => setActiveTab('staff')}
                  className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'staff' ? 'bg-black text-white' : 'text-gray-400 hover:text-black'}`}
                >
                  Assigned Staff
                </button>
              </div>
            </div>

            {activeTab === 'hours' ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                    <div key={day} className="border border-black/10 p-4 space-y-4">
                      <p className="ymca-brand-font text-xl uppercase tracking-wider text-center border-b border-black/5 pb-2">{day}</p>
                      <div className="space-y-3">
                         <div>
                            <label className="text-[8px] font-black text-gray-400 uppercase block mb-1">Opens</label>
                            <input type="time" defaultValue="00:00" className="w-full text-xs font-bold p-1 border border-black/10 focus:ring-0 focus:border-black" />
                         </div>
                         <div>
                            <label className="text-[8px] font-black text-gray-400 uppercase block mb-1">Closes</label>
                            <input type="time" defaultValue="23:59" className="w-full text-xs font-bold p-1 border border-black/10 focus:ring-0 focus:border-black" />
                         </div>
                         <label className="flex items-center space-x-2 cursor-pointer mt-4">
                            <input type="checkbox" className="w-3 h-3 border-2 border-black rounded-none checked:bg-black" />
                            <span className="text-[8px] font-bold uppercase tracking-widest">Closed</span>
                         </label>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-6 flex justify-end">
                   <button className="btn-primary">Update Hours</button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                {/* Assigned List */}
                <div className="space-y-6">
                  <h4 className="ymca-brand-font text-2xl uppercase border-b-2 border-black pb-2">Authorised Staff ({assignedStaff.length})</h4>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 scrollbar-hide">
                    {assignedStaff.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No staff assigned to this location.</p>
                    ) : assignedStaff.map(s => (
                      <div key={s.id} className="flex items-center justify-between p-3 border border-black/5 bg-gray-50">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-bold text-xs">{s.firstName[0]}</div>
                          <div>
                            <p className="text-[10px] font-black uppercase">{s.firstName} {s.lastName}</p>
                            <p className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter">{s.contractType}</p>
                          </div>
                        </div>
                        <button onClick={() => toggleStaffAssignment(s.id)} className="text-[8px] font-black text-red-500 uppercase hover:underline">Remove</button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pool List */}
                <div className="space-y-6">
                  <h4 className="ymca-brand-font text-2xl uppercase border-b-2 border-black pb-2">Staff Pool</h4>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 scrollbar-hide">
                    {availableStaff.map(s => (
                      <div key={s.id} className="flex items-center justify-between p-3 border border-black/10 hover:border-black transition-all group">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-ymca-neutral text-black flex items-center justify-center font-bold text-xs border border-black/10">{s.firstName[0]}</div>
                          <div>
                            <p className="text-[10px] font-black uppercase">{s.firstName} {s.lastName}</p>
                            <p className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter">{s.primaryLocationId}</p>
                          </div>
                        </div>
                        <button onClick={() => toggleStaffAssignment(s.id)} className="px-3 py-1 bg-black text-white text-[8px] font-black uppercase tracking-widest hover:bg-ymca-orange">Assign</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationSettingsView;
